/**
 * Structural data for the full-body-MRI page's new sections (AHD-6064).
 * No display strings live here — copy is in the `tests` i18n namespace,
 * keyed by the `keyBase` fields below (resolve as `tt(`${keyBase}Title`)`).
 */

/** The membership-first site's photo for a step (RELEASE_MEMBERSHIP_FIRST),
 *  in place of the live `image` on that site only (`mriStepForSite`). */
export interface MembershipFirstStepImage {
  image: string
  imageQuality?: number
}

export interface MriUspStep {
  /** i18n key base in the `tests` namespace, e.g. "mriUsp1" */
  keyBase: string
  /** placeholder label, shown when `image` is not set */
  placeholderLabel: string
  /** Blob path passed to `blobMediaUrl()`. When set, replaces the placeholder. */
  image?: string
  imageQuality?: number
  membershipFirst?: MembershipFirstStepImage
}

export const MRI_USP_STEPS: MriUspStep[] = [
  {
    keyBase: "mriUsp1",
    placeholderLabel: "Whole-body scan",
    image: "/images/mri/mri-usp1-scanner-screen-v2.jpg",
  },
  {
    keyBase: "mriUsp2",
    placeholderLabel: "European research cohorts",
    image: "/images/mri/mri-guidelines-octorad.png",
  },
  {
    keyBase: "mriUsp3",
    placeholderLabel: "AI quantification",
    image: "/images/homepage/service-mri-machine.jpeg",
    membershipFirst: {
      image:
        "/images/marketing/mri-scanner-background-20260930/mri-scanner-background-v1.png",
      imageQuality: 90,
    },
  },
]

export interface MriOrgan {
  /** stable id, also used in aria + test ids */
  id: string
  /** i18n key base in the `tests` namespace, e.g. "mriOrganBrain" */
  keyBase: string
  /** hotspot centre as a percentage of the body-illustration box
   *  (x = % of width, y = % of height). Tune these to the final
   *  anatomical illustration once it is in place. */
  x: number
  y: number
}

export const MRI_ORGANS: MriOrgan[] = [
  { id: "brain", keyBase: "mriOrganBrain", x: 50, y: 11 },
  { id: "neck", keyBase: "mriOrganNeck", x: 50, y: 20 },
  { id: "chest", keyBase: "mriOrganChest", x: 50, y: 32 },
  { id: "spine", keyBase: "mriOrganSpine", x: 53, y: 40 },
  { id: "liver", keyBase: "mriOrganLiver", x: 41, y: 46 },
  { id: "spleen", keyBase: "mriOrganSpleen", x: 59, y: 47 },
  { id: "pancreas", keyBase: "mriOrganPancreas", x: 50, y: 51 },
  { id: "kidneys", keyBase: "mriOrganKidneys", x: 43, y: 56 },
  { id: "bladder", keyBase: "mriOrganBladder", x: 50, y: 64 },
  { id: "pelvis", keyBase: "mriOrganPelvis", x: 50, y: 70 },
  { id: "mens", keyBase: "mriOrganMens", x: 45, y: 74 },
  { id: "womens", keyBase: "mriOrganWomens", x: 55, y: 74 },
]

export interface MriWalkthroughStep {
  keyBase: string
  placeholderLabel: string
  /** Blob path passed to `blobMediaUrl()`. When set, replaces the placeholder. */
  image?: string
  imageQuality?: number
  membershipFirst?: MembershipFirstStepImage
}

export const MRI_WALKTHROUGH_STEPS: MriWalkthroughStep[] = [
  {
    keyBase: "mriWalkStep1",
    placeholderLabel: "Clinic check-in",
    image: "/images/mri/walkthrough/step-checkin.png",
    membershipFirst: {
      image:
        "/images/marketing/mri-walkthrough-fresh-20260930/01-checkin-v1.png",
      imageQuality: 90,
    },
  },
  {
    keyBase: "mriWalkStep2",
    placeholderLabel: "The scan",
    image: "/images/mri/walkthrough/step-scan.png",
    membershipFirst: {
      image: "/images/marketing/mri-walkthrough-fresh-20260930/02-scan-v1.png",
      imageQuality: 90,
    },
  },
  {
    keyBase: "mriWalkStep3",
    placeholderLabel: "Radiologist analysis",
    image: "/images/mri/walkthrough/step-radiologist-v2.png",
    membershipFirst: {
      image:
        "/images/marketing/mri-walkthrough-fresh-20260930/03-radiologist-v1.png",
      imageQuality: 90,
    },
  },
  {
    keyBase: "mriWalkStep4",
    placeholderLabel: "Health report",
    image: "/images/mri/walkthrough/step-report-v2.png",
    membershipFirst: {
      image: "/images/marketing/hormone-women-film-20260930/03-report-v1.png",
      imageQuality: 90,
    },
  },
  {
    keyBase: "mriWalkStep5",
    placeholderLabel: "Doctor consultation",
    image: "/images/mri/walkthrough/step-consultation-home.jpg",
    membershipFirst: {
      image:
        "/images/marketing/mri-walkthrough-fresh-20260930/05-consultation-v1.png",
      imageQuality: 90,
    },
  },
]

/** `step` as `membershipFirstSite` (`isMembershipFirstSite(country)`) shows
 *  it: that site's photo where the step has one. */
export function mriStepForSite<
  T extends { image?: string; membershipFirst?: MembershipFirstStepImage },
>(step: T, membershipFirstSite: boolean): T {
  return membershipFirstSite && step.membershipFirst
    ? { ...step, ...step.membershipFirst }
    : step
}

export interface MriCredibilityItem {
  keyBase: string
}

export const MRI_CREDIBILITY_ITEMS: MriCredibilityItem[] = [
  { keyBase: "mriCred1" },
  { keyBase: "mriCred2" },
  { keyBase: "mriCred3" },
  { keyBase: "mriCred4" },
  { keyBase: "mriCred5" },
]
